/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Observed per-human delegation outcome, present only when the top-level status is observed. A configuration_failure count records past per-human failures for the current configuration; it is distinct from top-level configuration_failure, which reports a currently invalid configuration and returns no observations.
 */
export const DelegationStatusCountStatus = {
  DurableCredentialPresent: "durable_credential_present",
  AssertionOnly: "assertion_only",
  OfflineUnsupported: "offline_unsupported",
  OfflineNotRequested: "offline_not_requested",
  Refused: "refused",
  ReauthenticationRequired: "reauthentication_required",
  TemporaryFailure: "temporary_failure",
  ConfigurationFailure: "configuration_failure",
} as const;
/**
 * Observed per-human delegation outcome, present only when the top-level status is observed. A configuration_failure count records past per-human failures for the current configuration; it is distinct from top-level configuration_failure, which reports a currently invalid configuration and returns no observations.
 */
export type DelegationStatusCountStatus = ClosedEnum<
  typeof DelegationStatusCountStatus
>;

/**
 * Sanitized count of the latest observation per human, not a history of token requests.
 */
export type DelegationStatusCount = {
  /**
   * Number of humans with this latest outcome.
   */
  count: number;
  /**
   * Most recent credential acquisition, distinct from renewal.
   */
  lastCredentialObtainedAt?: Date | undefined;
  /**
   * Most recent matching observation.
   */
  lastObservedAt?: Date | undefined;
  /**
   * Most recent successful assertion renewal.
   */
  lastRefreshSucceededAt?: Date | undefined;
  /**
   * Observed per-human delegation outcome, present only when the top-level status is observed. A configuration_failure count records past per-human failures for the current configuration; it is distinct from top-level configuration_failure, which reports a currently invalid configuration and returns no observations.
   */
  status: DelegationStatusCountStatus;
};

/** @internal */
export const DelegationStatusCountStatus$inboundSchema: z.ZodMiniEnum<
  typeof DelegationStatusCountStatus
> = z.enum(DelegationStatusCountStatus);

/** @internal */
export const DelegationStatusCount$inboundSchema: z.ZodMiniType<
  DelegationStatusCount,
  unknown
> = z.pipe(
  z.object({
    count: z.int(),
    last_credential_obtained_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_observed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_refresh_succeeded_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    status: DelegationStatusCountStatus$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "last_credential_obtained_at": "lastCredentialObtainedAt",
      "last_observed_at": "lastObservedAt",
      "last_refresh_succeeded_at": "lastRefreshSucceededAt",
    });
  }),
);

export function delegationStatusCountFromJSON(
  jsonString: string,
): SafeParseResult<DelegationStatusCount, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => DelegationStatusCount$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'DelegationStatusCount' from JSON`,
  );
}
